import breakpoints from '../services/breakpoints';
import sizeMappings from '../services/sizeMappings';
import * as adManager from '../services/adManager';
import * as slotManager from '../services/slotManager';
import pubsub from '../utils/pubsub';
import perf from '../utils/performance';
import XandrAd from './XandrAd';

const ORIGINAL_ID_ATTRIBUTE = 'data-advert-orig-id',
	STATE_ATTRIBUTE = 'data-advert-slot-state',
	CLOSABLE_ATTRIBUTE = 'data-advert-closable',
	IS_EMPTY_ATTRIBUTE = 'data-advert-slot-empty';

function _validateSlotConfig(slotConfig) {
	function _validatePlacement(placement) {
		if (typeof placement !== 'string' && typeof placement !== 'number') {
			throw new Error('\'placement\' is required and should be a number, string or an object containing numbers or strings for each breakpoint.');
		}
	}

	if (typeof slotConfig.name !== 'string') {
		throw new Error('\'name\' is required and should be a string in slot config');
	}

	if (typeof slotConfig.placement === 'object') {
		Object.keys(breakpoints.getBreakpoints()).forEach((bp) => {
			_validatePlacement(slotConfig.placement[bp]);
		});
	} else {
		_validatePlacement(slotConfig.placement);
	}

	if (typeof slotConfig.sizeMapping !== 'string') {
		throw new Error('\'sizeMapping\' is required and should be a string inn slot config');
	}
}

function _sizesMatch(a, b) {
	return (a[0] === b[0] || a[0] === '*') && (a[1] === b[1] || a[1] === '*');
}

export enum SlotStateError {
	BAD_PLACEMENT = 'badPlacement',
	REQUEST_FAILURE = 'requestFailure',
	NODE_NOT_FOUND = 'nodeNotFound'
}

type SlotState = {
	defined?: boolean
	requested?: boolean
	rendered?: boolean
	showTagCalled?: boolean
	error?: SlotStateError
}

export default class Slot {
	state: SlotState;
	name: string;
	domId: string;
	targeting: Record<string, unknown>;
	placement: string | Record<string, string>;
	sizeMapping: string;
	sizeRemapping: Record<string, unknown>;
	preload: boolean | Record<string, boolean>;
	node: HTMLElement;
	renderBreakpoint: string;
	closeable: boolean;
	lazyLoad: boolean;
	lazyLoadThreshold: number | string | Record<string, number | string>;
	refreshable: boolean;
	refreshModes: Array<string>;
	refreshDelay: number;
	refreshThreshold: number | string;
	isEmpty: boolean | null;
	isMediated: boolean;

	// Ad Data
	adType: string;
	source: string;
	creativeId: string | number;
	targetId: string;
	tagId: number;
	buyerMemberId: number;
	auctionId: string | number;
	width: number;
	height: number;
	size: [number, number];
	originalSize: [number, number];

	constructor(slotConfig) {
		_validateSlotConfig(slotConfig);

		this.name = slotConfig.name;
		this.domId = _generateRandomSuffix(this.name);
		this.placement = slotConfig.placement;
		this.sizeMapping = slotConfig.sizeMapping;
		this.sizeRemapping = slotConfig.sizeRemapping;
		this.targeting = Object.assign({}, slotConfig.targeting);
		this.preload = typeof slotConfig.preload === 'undefined' ? true : slotConfig.preload;
		this.closeable = Boolean(slotConfig.closeable);
		this.node = null;
		this.renderBreakpoint = null;
		this.isEmpty = null;
		this.isMediated = false;

		this.lazyLoad = Boolean(slotConfig.lazyLoad);
		this.lazyLoadThreshold = slotConfig.lazyLoadThreshold;

		this.refreshable = Boolean(slotConfig.refresh);
		this.refreshThreshold = slotConfig.refreshThreshold;
		this.refreshDelay = slotConfig.refreshDelay;
		this.refreshModes = slotConfig.refreshModes;

		this.state = {};
		this.#publishStateEvents('created');
	}

	setDefined(value: boolean): void {
		this.#setMainState('defined', value);
	}

	setRequested(value: boolean): void {
		this.#setMainState('requested', value);
	}

	setRendered(value: boolean): void {
		this.#setMainState('rendered', value);
	}

	setShowTagCalled(value: boolean): void {
		this.state.showTagCalled = value;

		pubsub.publish(`slot.${this.name}`, this);
	}

	setMediated(value: boolean): void {
		this.isMediated = value;

		pubsub.publish(`slot.${this.name}`, this);
	}

	setError(errorType: SlotStateError): void {
		this.state.error = errorType;
		this.#publishStateEvents('error');
	}

	#setMainState(key, value) {
		this.state[key] = value;

		this.#updateNodeState();

		if (value) {
			this.#publishStateEvents(key);
		}
	}

	#updateNodeState() {
		if (this.node) {
			const nodeState = ['rendered', 'requested', 'defined'].find((state) => this.state[state]) ?? 'created';

			this.node.setAttribute(STATE_ATTRIBUTE, nodeState);

			if (this.isEmpty) {
				this.node.setAttribute(IS_EMPTY_ATTRIBUTE, '');
			} else {
				this.node.removeAttribute(IS_EMPTY_ATTRIBUTE);
			}
		}
	}

	#publishStateEvents(state) {
		perf.mark(`xandr - slot - ${this.name} - ${state}`);

		pubsub.publish(`slot.${state}`, this);
		pubsub.publish(`slot.${this.name}`, this);
		pubsub.publish(`slot.${this.name}.${state}`, this);
	}

	#updateRenderData(xandrAd: XandrAd) {
		Object.assign(this, xandrAd);
		this.renderBreakpoint = breakpoints.getCurrentBreakpoint();

		this.#updateNodeState();
	}

	#remapSize() {
		const sizeRemapping = this.sizeRemapping ?? sizeMappings.getSizeMapping(this.sizeMapping)?.sizeRemapping;

		if (sizeRemapping?.[this.adType]) {
			sizeRemapping[this.adType].forEach(([before, after]) => {
				if (_sizesMatch(before, this.size)) {
					adManager.resizeSlot(this, after);
					this.originalSize = this.size;
					this.width = after[0];
					this.height = after[1];
					this.size = [this.width, this.height];
				}
			});
		}
	}

	getPreload() {
		// Breakpoint based preload
		if (typeof this.preload === 'object') {
			const currentBp = breakpoints.getCurrentBreakpoint();

			return typeof this.preload[currentBp] === 'undefined' ? this.preload.default : this.preload[currentBp];
		}

		return this.preload;
	}

	updateNode(node) {
		this.removeNode();

		this.node = node;
		this.node.setAttribute(ORIGINAL_ID_ATTRIBUTE, this.node.id);
		this.node.id = this.domId;

		this.#updateNodeState();

		pubsub.publish(`slot.${this.name}`, this);
	}

	removeNode() {
		if (this.node) {
			this.node.id = this.node.getAttribute(ORIGINAL_ID_ATTRIBUTE);
			this.node.removeAttribute(ORIGINAL_ID_ATTRIBUTE);
			this.node.removeAttribute(IS_EMPTY_ATTRIBUTE);
			this.node.removeAttribute(STATE_ATTRIBUTE);
			this.node.removeAttribute(CLOSABLE_ATTRIBUTE);
			this.node.innerHTML = '';
			this.node = null;
		}
	}

	refresh(reason = 'other') {
		slotManager.refreshSlots([this], reason);

		return this;
	}

	setAdData(adObj) {
		this.#updateRenderData(new XandrAd(adObj));
		this.#remapSize();

		pubsub.publish(`slot.${this.name}`, this);
	}
}

function _generateRandomSuffix(base) {
	return `${base}_${Math.round(Math.random() * 1e18)}`;
}
